/**
 * Canasta — server-authoritative rules engine (partnership meld game, 4 players), simplified but faithful.
 *
 * Rules implemented (toggles live in {@link CanastaConfig}):
 *  - 4 players in two partnerships: seats 0 & 2 (team 0) against seats 1 & 3 (team 1). Two 52-card decks plus four
 *    printed jokers (108 cards), 11 cards each. The player left of the dealer starts. Twos and jokers are wild.
 *  - Red threes are laid down automatically (when dealt or drawn) and replaced from the stock: 100 each, 800 for all
 *    four, but they count against a partnership that has no meld at the end of the hand.
 *  - A turn is: draw (two cards from the stock, or take the discard pile), then meld / add to melds (any number of
 *    `meld` actions), then discard one card (or go out).
 *  - A meld is three or more cards of one rank (4..A) with at most three wild cards and fewer wild cards than natural
 *    cards. Black threes may only be melded when going out (natural only). A meld of seven or more cards is a canasta:
 *    500 bonus when natural, 300 when it holds wild cards.
 *  - A partnership's first meld of a hand must be worth at least 15 / 50 / 90 / 120 card points depending on its score
 *    at the start of the hand (below 0 / 0–1499 / 1500–2999 / 3000+). The initial meld must be made in one action.
 *  - Taking the pile needs the top card to be a natural card of rank 4..A and the partnership to have melded already
 *    (simplification: a partnership with no meld cannot take the pile). A pile holding a wild card is frozen: it can
 *    only be taken with two natural cards of the top rank. Otherwise two cards (one natural, the rest natural or wild)
 *    do it — one card is enough when the partnership already has a meld of that rank. The top card and the cards played
 *    from the hand form (or extend) a meld; the rest of the pile goes into the hand.
 *  - Discarding a wild card freezes the pile; discarding a black three blocks the next player from taking it.
 *  - Going out needs a partnership canasta and ends the hand (+100). Melding or discarding the last card is only
 *    allowed with a canasta, so a player always keeps a card to discard. When the stock is empty at the start of a draw
 *    the hand ends. Unmelded cards count against their partnership.
 *  - The match ends when a partnership reaches `targetScore` and leads; `maxHands` is a hard cap (higher total wins).
 *    Partners share a place, so payouts are split between the two partners.
 */
import { buildDeck, parseCard, removeCards, sortHand, type CardId } from '../../core/cards.js';
import { checkInt, mergeKnown } from '../../core/config.js';
import { shuffle, type Rng } from '../../core/rng.js';
import {
  assertEngine,
  EngineError,
  isPlainObject,
  type EngineEvent,
  type GameEngine,
  type Standing,
} from '../../core/types.js';

export interface CanastaConfig {
  /** Match ends when a partnership reaches this score and leads. */
  targetScore: number;
  /** Hard cap on hands so a match always terminates. */
  maxHands: number;
  handEndDelayMs: number;
}

export type CanastaPhase = 'DRAW' | 'PLAY' | 'HAND_END' | 'MATCH_END';

/** One partnership meld: cards of a single rank plus any wild cards. */
export interface CanastaMeld {
  rank: number;
  cards: CardId[];
}

export interface CanastaMeldGroup {
  rank: number;
  cards: CardId[];
}

/** Score breakdown of one partnership's hand (public once the hand ends). */
export interface CanastaTeamHand {
  /** Card points of every card in melds. */
  melds: number;
  /** 500 per natural canasta, 300 per mixed one. */
  canastas: number;
  /** Red three bonus (negative when the partnership has no meld). */
  redThrees: number;
  /** 100 for going out. */
  goOut: number;
  /** Card points left in the partnership's hands (subtracted). */
  hands: number;
  total: number;
}

export interface CanastaState {
  config: CanastaConfig;
  players: string[];
  hand: number;
  dealer: number;
  phase: CanastaPhase;
  hands: CardId[][];
  stock: CardId[];
  /** Discard pile, top card last. */
  pile: CardId[];
  /** True while the pile holds a wild card. */
  pileFrozen: boolean;
  /** Melds per team (index = seat % 2). */
  melds: CanastaMeld[][];
  redThrees: CardId[][];
  /** True once a team has made its initial meld this hand. */
  melded: boolean[];
  /** Minimum card points for each team's initial meld this hand. */
  initialMin: number[];
  turn: number;
  /** Seat that went out this hand (null while playing or after a stock-out). */
  wentOut: number | null;
  teamScores: number[];
  handResults: CanastaTeamHand[][];
}

export type CanastaAction =
  | { type: 'draw' }
  | { type: 'takePile'; cards: CardId[] }
  | { type: 'meld'; groups: CanastaMeldGroup[] }
  | { type: 'discard'; card: CardId };

export type CanastaLegal =
  | { kind: 'draw'; canTakePile: boolean; frozen: boolean }
  | { kind: 'play'; discard: CardId[] }
  | { kind: 'none' };

export interface CanastaView {
  gameType: 'CANASTA';
  config: CanastaConfig;
  seat: number | null;
  phase: CanastaPhase;
  round: number;
  dealer: number;
  turn: number;
  hand: CardId[];
  handCounts: number[];
  stockCount: number;
  pile: CardId[];
  pileFrozen: boolean;
  melds: CanastaMeld[][];
  redThrees: CardId[][];
  melded: boolean[];
  initialMin: number[];
  wentOut: number | null;
  teamScores: number[];
  handResults: CanastaTeamHand[][];
  /** Score of each seat's team (so generic score UIs work). */
  totals: number[];
}

const DEFAULTS: CanastaConfig = {
  targetScore: 5000,
  maxHands: 6,
  handEndDelayMs: 6000,
};

const SEATS = 4;
const HAND_SIZE = 11;
const DRAW_COUNT = 2;
const MAX_WILDS = 3;
const CANASTA_SIZE = 7;
const NATURAL_CANASTA = 500;
const MIXED_CANASTA = 300;
const RED_THREE = 100;
const ALL_RED_THREES = 800;
const GO_OUT_BONUS = 100;

export const canastaTeamOf = (seat: number): number => seat % 2;
export const canastaPartnerOf = (seat: number): number => (seat + 2) % SEATS;

export const canastaIsWild = (id: CardId): boolean => {
  const c = parseCard(id);
  return c.joker || c.rank === 2;
};
export const canastaIsRedThree = (id: CardId): boolean => {
  const c = parseCard(id);
  return !c.joker && c.rank === 3 && (c.suit === 'H' || c.suit === 'D');
};

/** Card points: joker 50, 2 and ace 20, K–8 10, 7–4 and black 3 5. */
export function canastaCardPoints(id: CardId): number {
  const c = parseCard(id);
  if (c.joker) return 50;
  if (c.rank === 2 || c.rank === 14) return 20;
  if (c.rank >= 8) return 10;
  return 5;
}

export const canastaMeldPoints = (cards: readonly CardId[]): number => cards.reduce((n, c) => n + canastaCardPoints(c), 0);

export const canastaIsCanasta = (m: CanastaMeld): boolean => m.cards.length >= CANASTA_SIZE;
export const canastaIsNaturalCanasta = (m: CanastaMeld): boolean => canastaIsCanasta(m) && !m.cards.some(canastaIsWild);
export const canastaHasCanasta = (melds: readonly CanastaMeld[]): boolean => melds.some(canastaIsCanasta);

/** Minimum card points for an initial meld, by the partnership's score. */
export function canastaInitialMin(score: number): number {
  if (score < 0) return 15;
  if (score < 1500) return 50;
  if (score < 3000) return 90;
  return 120;
}

/** Null when `cards` form a legal meld of `rank`; otherwise why not. */
export function canastaMeldError(rank: number, cards: readonly CardId[]): string | null {
  if (!Number.isInteger(rank) || rank < 3 || rank > 14) return 'Bad rank';
  if (cards.length < 3) return 'A meld needs at least three cards';
  const wilds = cards.filter(canastaIsWild);
  const naturals = cards.filter((c) => !canastaIsWild(c));
  if (naturals.some((c) => parseCard(c).rank !== rank)) return 'All natural cards must match the rank';
  if (rank === 3) return wilds.length > 0 ? 'Black threes cannot use wild cards' : null;
  if (wilds.length > MAX_WILDS) return 'A meld can have at most three wild cards';
  if (wilds.length >= naturals.length) return 'A meld needs more natural cards than wild cards';
  return null;
}

/**
 * Checks a `meld` action against the player's hand. `groups` add to a team meld of the same rank or start a new one.
 * Returns an error message, or null when legal.
 */
export function canastaCheckMelds(args: {
  hand: readonly CardId[];
  groups: readonly CanastaMeldGroup[];
  teamMelds: readonly CanastaMeld[];
  melded: boolean;
  minMeld: number;
}): string | null {
  const { hand, groups, teamMelds, melded, minMeld } = args;
  if (groups.length === 0) return 'Nothing to meld';
  if (new Set(groups.map((g) => g.rank)).size !== groups.length) return 'One group per rank';
  const rest = [...hand];
  const used: CardId[] = [];
  let canasta = canastaHasCanasta(teamMelds);
  let initialPoints = 0;
  let goingOutOnly = false;
  for (const g of groups) {
    if (g.cards.length === 0) return 'Empty group';
    if (!removeCards(rest, g.cards)) return 'Card not in hand';
    used.push(...g.cards);
    const existing = teamMelds.find((m) => m.rank === g.rank);
    const combined = existing ? [...existing.cards, ...g.cards] : g.cards;
    const err = canastaMeldError(g.rank, combined);
    if (err) return err;
    if (g.rank === 3) goingOutOnly = true;
    else initialPoints += canastaMeldPoints(g.cards);
    if (combined.length >= CANASTA_SIZE) canasta = true;
  }
  if (!melded && initialPoints < minMeld) return `Your first meld must be worth at least ${minMeld} points`;
  if (!melded && teamMelds.length > 0) return 'Unexpected melds';
  if (goingOutOnly && (!canasta || rest.length > 1)) return 'Black threes can only be melded when going out';
  if (rest.length === 0 && !canasta) return 'You need a canasta to go out';
  if (rest.length === 1 && !canasta) return 'Keep two cards until you have a canasta';
  return null;
}

/** Null when `cards` (from the hand) legally take a pile with this top card; otherwise why not. */
export function canastaTakeError(args: {
  hand: readonly CardId[];
  pile: readonly CardId[];
  frozen: boolean;
  teamMelds: readonly CanastaMeld[];
  melded: boolean;
  cards: readonly CardId[];
}): string | null {
  const { hand, pile, frozen, teamMelds, melded, cards } = args;
  const top = pile.at(-1);
  if (top === undefined) return 'The pile is empty';
  const t = parseCard(top);
  if (canastaIsWild(top) || t.rank < 4) return 'The top card blocks the pile';
  if (!melded) return 'Make your first meld before taking the pile';
  const rest = [...hand];
  if (!removeCards(rest, cards)) return 'Card not in hand';
  const existing = teamMelds.find((m) => m.rank === t.rank);
  if (frozen) {
    if (cards.some((c) => canastaIsWild(c) || parseCard(c).rank !== t.rank) || cards.length < 2) {
      return 'A frozen pile needs two natural cards of the top rank';
    }
  } else {
    if (cards.some((c) => !canastaIsWild(c) && parseCard(c).rank !== t.rank)) return 'Cards must match the top card or be wild';
    if (!existing && (cards.length < 2 || cards.every(canastaIsWild))) return 'Take the pile with two cards, one of them natural';
    if (existing && cards.length < 1) return 'Play at least one card';
  }
  return canastaMeldError(t.rank, [...(existing?.cards ?? []), top, ...cards]);
}

/** Scores one partnership's hand. */
export function canastaScoreTeam(args: { melds: readonly CanastaMeld[]; redThrees: readonly CardId[]; handCards: readonly CardId[]; wentOut: boolean }): CanastaTeamHand {
  const { melds, redThrees, handCards, wentOut } = args;
  const meldPts = melds.reduce((n, m) => n + canastaMeldPoints(m.cards), 0);
  const canastas = melds.reduce((n, m) => n + (canastaIsCanasta(m) ? (canastaIsNaturalCanasta(m) ? NATURAL_CANASTA : MIXED_CANASTA) : 0), 0);
  const redBase = redThrees.length === 4 ? ALL_RED_THREES : redThrees.length * RED_THREE;
  const red = melds.length > 0 ? redBase : -redBase;
  const goOut = wentOut ? GO_OUT_BONUS : 0;
  const hands = handCards.reduce((n, c) => n + (canastaIsRedThree(c) ? 0 : canastaCardPoints(c)), 0);
  return { melds: meldPts, canastas, redThrees: red, goOut, hands, total: meldPts + canastas + red + goOut - hands };
}

/** Pulls red threes out of `seat`'s hand into its team's pile, replacing each from the stock. */
function layRedThrees(state: CanastaState, seat: number, events: EngineEvent[]): void {
  const hand = state.hands[seat]!;
  for (let guard = 0; guard < 8; guard++) {
    const reds = hand.filter(canastaIsRedThree);
    if (reds.length === 0) return;
    removeCards(hand, reds);
    state.redThrees[canastaTeamOf(seat)]!.push(...reds);
    events.push({ type: 'redThree', seat, cards: reds });
    for (let i = 0; i < reds.length && state.stock.length > 0; i++) hand.push(state.stock.pop()!);
  }
}

function deal(state: CanastaState, rng: Rng, events: EngineEvent[]): void {
  const deck = shuffle(buildDeck({ decks: 2, printedJokersPerDeck: 2 }), rng);
  state.hands = Array.from({ length: SEATS }, (_, i) => deck.slice(i * HAND_SIZE, (i + 1) * HAND_SIZE));
  state.stock = deck.slice(SEATS * HAND_SIZE);
  state.pile = [];
  state.pileFrozen = false;
  state.melds = [[], []];
  state.redThrees = [[], []];
  state.melded = [false, false];
  state.initialMin = state.teamScores.map(canastaInitialMin);
  state.wentOut = null;
  state.turn = (state.dealer + 1) % SEATS;
  state.phase = 'DRAW';
  for (let s = 0; s < SEATS; s++) layRedThrees(state, (state.dealer + 1 + s) % SEATS, events);
  // Turn up the first pile card: skip (and bury) wild cards, threes; a skipped wild card freezes the pile.
  while (state.stock.length > 0) {
    const c = state.stock.pop()!;
    if (canastaIsRedThree(c)) {
      state.redThrees[canastaTeamOf(state.turn)]!.push(c);
      continue;
    }
    state.pile.push(c);
    if (canastaIsWild(c)) state.pileFrozen = true;
    if (!canastaIsWild(c) && parseCard(c).rank >= 4) break;
  }
  state.hands = state.hands.map((h) => sortHand(h));
  events.push({ type: 'dealt', hand: state.hand, dealer: state.dealer, counts: state.hands.map((h) => h.length), pileTop: state.pile.at(-1) });
}

/** Partners share a place: the leading team is 1st, the other 2nd (both 1st on a tie). */
function teamStandings(state: CanastaState): Standing[] {
  const [a, b] = state.teamScores as [number, number];
  return [0, 1, 2, 3].map((seat) => {
    const team = canastaTeamOf(seat);
    const mine = team === 0 ? a : b;
    const theirs = team === 0 ? b : a;
    return { seat, score: mine, place: mine >= theirs ? 1 : 2 };
  });
}

function finishHand(state: CanastaState, wentOut: number | null, events: EngineEvent[]): void {
  state.wentOut = wentOut;
  const results = [0, 1].map((team) =>
    canastaScoreTeam({
      melds: state.melds[team]!,
      redThrees: state.redThrees[team]!,
      handCards: state.hands.filter((_, s) => canastaTeamOf(s) === team).flat(),
      wentOut: wentOut !== null && canastaTeamOf(wentOut) === team,
    }),
  );
  results.forEach((r, team) => {
    state.teamScores[team]! += r.total;
  });
  state.handResults.push(results);
  events.push({ type: 'handEnd', hand: state.hand, wentOut, results, teamScores: [...state.teamScores] });
  const [a, b] = state.teamScores as [number, number];
  const reached = a >= state.config.targetScore || b >= state.config.targetScore;
  const capped = state.hand >= state.config.maxHands;
  if ((reached && a !== b) || capped) {
    state.phase = 'MATCH_END';
    events.push({ type: 'matchEnd', standings: teamStandings(state) });
  } else {
    state.phase = 'HAND_END';
  }
}

function addToMelds(state: CanastaState, team: number, group: CanastaMeldGroup): void {
  const existing = state.melds[team]!.find((m) => m.rank === group.rank);
  if (existing) existing.cards.push(...group.cards);
  else state.melds[team]!.push({ rank: group.rank, cards: [...group.cards] });
  state.melds[team]!.sort((x, y) => y.rank - x.rank);
}

/** True when the seat's team could take the pile with some cards from the hand (used for legal-action hints). */
function canTakeSomething(state: CanastaState, seat: number): boolean {
  const top = state.pile.at(-1);
  if (top === undefined || canastaIsWild(top) || parseCard(top).rank < 4) return false;
  const team = canastaTeamOf(seat);
  if (!state.melded[team]) return false;
  const rank = parseCard(top).rank;
  const hand = state.hands[seat]!;
  const naturals = hand.filter((c) => !canastaIsWild(c) && parseCard(c).rank === rank);
  const wilds = hand.filter(canastaIsWild);
  const existing = state.melds[team]!.some((m) => m.rank === rank);
  if (state.pileFrozen) return naturals.length >= 2;
  return existing ? naturals.length >= 1 || wilds.length >= 1 : naturals.length >= 1 && naturals.length + wilds.length >= 2;
}

export const canastaEngine: GameEngine<CanastaConfig, CanastaState, CanastaAction, CanastaView, CanastaLegal> = {
  gameType: 'CANASTA',
  kind: 'match',

  defaultConfig: () => ({ ...DEFAULTS }),

  normalizeConfig(input) {
    const c = mergeKnown(DEFAULTS, input);
    checkInt('targetScore', c.targetScore, 100, 20000);
    checkInt('maxHands', c.maxHands, 1, 20);
    checkInt('handEndDelayMs', c.handEndDelayMs, 0, 60000);
    return c;
  },

  seatLimits: () => ({ min: SEATS, max: SEATS }),

  createState(config, seats, rng) {
    assertEngine(seats.length === SEATS, 'INVALID_CONFIG', 'Canasta needs exactly 4 seats');
    const players = Array<string>(SEATS).fill('');
    for (const s of seats) {
      assertEngine(s.seat >= 0 && s.seat < SEATS && !players[s.seat], 'SEAT_TAKEN', `Bad seat ${s.seat}`);
      players[s.seat] = s.playerId;
    }
    const state: CanastaState = {
      config,
      players,
      hand: 1,
      dealer: rng.int(SEATS),
      phase: 'DRAW',
      hands: [],
      stock: [],
      pile: [],
      pileFrozen: false,
      melds: [[], []],
      redThrees: [[], []],
      melded: [false, false],
      initialMin: [50, 50],
      turn: 0,
      wentOut: null,
      teamScores: [0, 0],
      handResults: [],
    };
    deal(state, rng, []);
    return state;
  },

  parseAction(raw) {
    if (!isPlainObject(raw)) throw new EngineError('INVALID_ACTION', 'Action must be an object');
    const cardList = (v: unknown): CardId[] => {
      if (!Array.isArray(v) || v.length > 60 || v.some((c) => typeof c !== 'string' || c.length > 6)) throw new EngineError('INVALID_ACTION', 'Bad card list');
      return v as CardId[];
    };
    if (raw.type === 'draw') return { type: 'draw' };
    if (raw.type === 'takePile') return { type: 'takePile', cards: cardList(raw.cards) };
    if (raw.type === 'discard' && typeof raw.card === 'string' && raw.card.length <= 6) return { type: 'discard', card: raw.card };
    if (raw.type === 'meld' && Array.isArray(raw.groups) && raw.groups.length >= 1 && raw.groups.length <= 12) {
      const groups = raw.groups.map((g): CanastaMeldGroup => {
        if (!isPlainObject(g) || typeof g.rank !== 'number') throw new EngineError('INVALID_ACTION', 'Bad meld group');
        return { rank: g.rank, cards: cardList(g.cards) };
      });
      return { type: 'meld', groups };
    }
    throw new EngineError('INVALID_ACTION', 'Unknown Canasta action');
  },

  applyAction(state, seat, action) {
    assertEngine(state.phase !== 'MATCH_END' && state.phase !== 'HAND_END', 'GAME_OVER', 'Hand is over');
    assertEngine(seat === state.turn, 'NOT_YOUR_TURN', 'Not your turn');
    const events: EngineEvent[] = [];
    const team = canastaTeamOf(seat);
    const hand = state.hands[seat]!;

    if (action.type === 'draw') {
      assertEngine(state.phase === 'DRAW', 'ILLEGAL_MOVE', 'Already drew');
      assertEngine(state.stock.length > 0, 'ILLEGAL_MOVE', 'The stock is empty');
      const n = Math.min(DRAW_COUNT, state.stock.length);
      for (let i = 0; i < n; i++) hand.push(state.stock.pop()!);
      events.push({ type: 'draw', seat, count: n });
      layRedThrees(state, seat, events);
      state.hands[seat] = sortHand(hand);
      state.phase = 'PLAY';
      return events;
    }

    if (action.type === 'takePile') {
      assertEngine(state.phase === 'DRAW', 'ILLEGAL_MOVE', 'Already drew');
      const err = canastaTakeError({ hand, pile: state.pile, frozen: state.pileFrozen, teamMelds: state.melds[team]!, melded: state.melded[team]!, cards: action.cards });
      assertEngine(!err, 'ILLEGAL_MOVE', err ?? '');
      const top = state.pile.pop()!;
      const rank = parseCard(top).rank;
      removeCards(hand, action.cards);
      addToMelds(state, team, { rank, cards: [top, ...action.cards] });
      const taken = state.pile.length;
      hand.push(...state.pile);
      state.pile = [];
      state.pileFrozen = false;
      state.hands[seat] = sortHand(hand);
      state.phase = 'PLAY';
      events.push({ type: 'takePile', seat, rank, melded: [top, ...action.cards], taken });
      return events;
    }

    assertEngine(state.phase === 'PLAY', 'ILLEGAL_MOVE', 'Draw first');

    if (action.type === 'meld') {
      const err = canastaCheckMelds({ hand, groups: action.groups, teamMelds: state.melds[team]!, melded: state.melded[team]!, minMeld: state.initialMin[team]! });
      assertEngine(!err, 'ILLEGAL_MOVE', err ?? '');
      for (const g of action.groups) {
        removeCards(hand, g.cards);
        addToMelds(state, team, g);
      }
      state.melded[team] = true;
      events.push({ type: 'meld', seat, groups: action.groups });
      if (hand.length === 0) finishHand(state, seat, events);
      return events;
    }

    // discard
    assertEngine(hand.includes(action.card), 'INVALID_CARD', 'Card not in hand');
    assertEngine(!canastaIsRedThree(action.card), 'ILLEGAL_MOVE', 'Red threes cannot be discarded');
    assertEngine(hand.length > 1 || canastaHasCanasta(state.melds[team]!), 'ILLEGAL_MOVE', 'You need a canasta to go out');
    removeCards(hand, [action.card]);
    state.pile.push(action.card);
    if (canastaIsWild(action.card)) state.pileFrozen = true;
    events.push({ type: 'discard', seat, card: action.card });
    if (hand.length === 0) {
      finishHand(state, seat, events);
      return events;
    }
    state.turn = (seat + 1) % SEATS;
    state.phase = 'DRAW';
    return events;
  },

  getActors(state) {
    if (state.phase !== 'DRAW' && state.phase !== 'PLAY') return [];
    if (state.phase === 'DRAW' && state.stock.length === 0) return [];
    return [state.turn];
  },

  getLegalActions(state, seat) {
    if (seat !== state.turn) return { kind: 'none' };
    if (state.phase === 'DRAW') {
      if (state.stock.length === 0) return { kind: 'none' };
      return { kind: 'draw', canTakePile: canTakeSomething(state, seat), frozen: state.pileFrozen };
    }
    if (state.phase === 'PLAY') {
      const hand = state.hands[seat]!;
      const canOut = canastaHasCanasta(state.melds[canastaTeamOf(seat)]!);
      return { kind: 'play', discard: hand.length > 1 || canOut ? [...hand] : [] };
    }
    return { kind: 'none' };
  },

  getTimeoutAction(state, seat) {
    if (state.phase === 'DRAW') return { type: 'draw' };
    const hand = state.hands[seat]!;
    // Cheapest non-wild card, black threes first (they block the pile).
    const ordered = [...hand].sort((a, b) => {
      const key = (c: CardId) => (canastaIsWild(c) ? 1000 : 0) + (parseCard(c).rank === 3 ? -100 : canastaCardPoints(c));
      return key(a) - key(b);
    });
    return { type: 'discard', card: ordered[0]! };
  },

  getView(state, seat) {
    return {
      gameType: 'CANASTA',
      config: state.config,
      seat,
      phase: state.phase,
      round: state.hand,
      dealer: state.dealer,
      turn: state.turn,
      hand: seat === null ? [] : [...state.hands[seat]!],
      handCounts: state.hands.map((h) => h.length),
      stockCount: state.stock.length,
      pile: [...state.pile],
      pileFrozen: state.pileFrozen,
      melds: state.melds.map((ms) => ms.map((m) => ({ rank: m.rank, cards: [...m.cards] }))),
      redThrees: state.redThrees.map((r) => [...r]),
      melded: [...state.melded],
      initialMin: [...state.initialMin],
      wentOut: state.wentOut,
      teamScores: [...state.teamScores],
      handResults: state.handResults.map((r) => r.map((x) => ({ ...x }))),
      totals: [0, 1, 2, 3].map((s) => state.teamScores[canastaTeamOf(s)]!),
    };
  },

  getSystemStep(state) {
    if (state.phase === 'DRAW' && state.stock.length === 0) return { kind: 'endHand', delayMs: 1200 };
    if (state.phase === 'HAND_END') return { kind: 'nextHand', delayMs: state.config.handEndDelayMs };
    return null;
  },

  runSystemStep(state, rng) {
    const events: EngineEvent[] = [];
    if (state.phase === 'DRAW' && state.stock.length === 0) {
      finishHand(state, null, events);
      return events;
    }
    if (state.phase === 'HAND_END') {
      state.hand++;
      state.dealer = (state.dealer + 1) % SEATS;
      deal(state, rng, events);
    }
    return events;
  },

  isFinished: (state) => state.phase === 'MATCH_END',

  getStandings(state): Standing[] {
    return teamStandings(state);
  },

  getRoundsCompleted: (state) => state.handResults.length,
};
